import { ChevronDown, Check, ArrowRight } from 'lucide-react';

const CALENDLY_URL = 'https://calendly.com/matthew-trottstudios';

/**
 * ServiceCardImage - Renders the image section of the service card
 */
const ServiceCardImage = ({ service, isExpanded }) => (
  <div className="relative h-40 overflow-hidden">
    <img 
      src={service.image} 
      alt={service.title}
      className={`w-full h-full object-cover transition-all duration-700 ${
        isExpanded ? 'scale-110' : 'group-hover:scale-110 group-hover:rotate-1'
      }`}
    />
    <div className="absolute inset-0 bg-gradient-to-t from-[#1E1E2E] via-[#1E1E2E]/50 to-transparent" />
    
    {/* Stat badge */}
    <div className={`absolute bottom-3 left-3 bg-[#00D4FF] rounded-xl px-3 py-2 transition-all ${
      isExpanded ? 'animate-pulse-glow' : ''
    }`}>
      <p className="text-[#1E1E2E] font-bold text-lg leading-none">{service.stat}</p>
      <p className="text-[#1E1E2E]/70 text-[10px]">{service.statLabel}</p>
    </div>
    
    {/* Expand indicator */}
    <div className={`absolute top-3 right-3 w-8 h-8 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center transition-all duration-300 ${
      isExpanded ? 'bg-[#00D4FF] rotate-180' : 'group-hover:bg-[#00D4FF]/50'
    }`}>
      <ChevronDown size={16} className={`${isExpanded ? 'text-[#1E1E2E]' : 'text-white'}`} />
    </div>
  </div>
);

/**
 * ServiceCardContent - Renders the title and description
 */
const ServiceCardContent = ({ service, isExpanded }) => {
  const IconComponent = service.icon;
  
  return (
    <div className="p-5 relative">
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors duration-300 ${
          isExpanded ? 'bg-[#00D4FF]' : 'bg-[#00D4FF]/10 group-hover:bg-[#00D4FF]'
        }`}>
          <IconComponent size={16} className={`transition-colors ${
            isExpanded ? 'text-[#1E1E2E]' : 'text-[#00D4FF] group-hover:text-[#1E1E2E]'
          }`} strokeWidth={1.5} />
        </div>
        <h3
          className={`text-lg font-semibold transition-colors ${
            isExpanded ? 'text-[#00D4FF]' : 'text-white group-hover:text-[#00D4FF]'
          }`}
          style={{ fontFamily: 'Manrope, sans-serif' }}
        >
          {service.title}
        </h3>
      </div>
      <p className="text-sm text-gray-400 group-hover:text-gray-300 transition-colors">
        {service.description}
      </p>
    </div>
  );
};

/**
 * ServiceCardDetails - Renders the expandable details section
 */
const ServiceCardDetails = ({ service, isExpanded }) => (
  <div className={`overflow-hidden transition-all duration-500 ease-in-out ${
    isExpanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
  }`}>
    <div className="px-5 pb-6 pt-2 border-t border-[#3D3D50]">
      {/* Intro text */}
      <p className="text-sm text-gray-300 mb-4 leading-relaxed">
        {service.details.intro}
      </p>
      
      {/* What's included */}
      <p className="text-xs font-semibold text-[#00D4FF] uppercase tracking-wider mb-3">
        What&apos;s Included
      </p>
      <ul className="grid grid-cols-1 gap-2 mb-5">
        {service.details.includes.map((item, idx) => (
          <li 
            key={`${service.id}-include-${idx}`}
            className="flex items-center gap-2 text-sm text-gray-400"
            style={{ 
              animation: isExpanded ? `fadeInUp 0.3s ease-out ${idx * 0.05}s forwards` : 'none',
              opacity: isExpanded ? 0 : 1
            }}
          >
            <div className="w-5 h-5 rounded-full bg-[#00D4FF]/10 flex items-center justify-center flex-shrink-0">
              <Check size={12} className="text-[#00D4FF]" />
            </div>
            {item}
          </li>
        ))}
      </ul>
      
      {/* CTA Button */}
      <a
        href={CALENDLY_URL}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`service-cta-${service.id}`}
        className="inline-flex items-center justify-center gap-2 w-full rounded-xl px-5 py-3 text-sm font-semibold bg-[#00D4FF] text-[#1E1E2E] hover:bg-[#00B8E6] transition-all duration-300 hover:scale-[1.02] shadow-lg shadow-[#00D4FF]/20"
        onClick={(e) => e.stopPropagation()}
      >
        {service.details.cta}
        <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
      </a>
    </div>
  </div>
);

/**
 * ServiceCard - Main component for individual service cards
 */
export const ServiceCard = ({ service, isExpanded, onToggle }) => {
  return (
    <div
      data-testid={`service-card-${service.id}`}
      className={`group relative bg-[#2D2D3F] rounded-2xl overflow-hidden border transition-all duration-500 ${
        isExpanded 
          ? 'border-[#00D4FF] shadow-lg shadow-[#00D4FF]/20' 
          : 'border-[#3D3D50] hover:border-[#00D4FF]/50'
      }`}
    >
      {/* Main card content - clickable */}
      <div 
        onClick={onToggle}
        className="cursor-pointer"
      >
        {/* Hover glow effect */}
        <div className={`absolute inset-0 bg-gradient-to-t from-[#00D4FF]/10 to-transparent transition-opacity duration-500 ${
          isExpanded ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
        }`} />
        
        <ServiceCardImage service={service} isExpanded={isExpanded} />
        <ServiceCardContent service={service} isExpanded={isExpanded} />
      </div>
      
      <ServiceCardDetails service={service} isExpanded={isExpanded} />
    </div>
  );
};

export default ServiceCard;
